/**
 * MergeFieldEditor
 *
 * A rich text editor (built on top of Tiptap) that adds merge-field support:
 *   - an "Insert field ▼" dropdown in the toolbar (grouped by category),
 *   - a `{`-triggered autocomplete with auto-closing brace + fuzzy matching,
 *   - an imperative `insertToken()` handle so a side panel can insert a chip at
 *     the real cursor position (no HTML string-append hacks).
 *
 * Chips are stored in the HTML content as:
 *   <span data-merge-token="{client.name}" class="merge-chip">[Client Name]</span>
 *
 * In the builder preview (no real context) the chip text acts as the friendly
 * placeholder label. In the wizard / print output the parent resolves the HTML
 * with `resolveTokensInHtml()` to substitute real values.
 */

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  RemoveFormatting,
  ChevronDown,
  Tag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { MergeToken } from '@/services/customerTermsService';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  filterTokens,
  tokenDisplayName,
  type MergeTokenCategory,
} from '@/services/mergeTokens';

// A shared id prefix for autocomplete option elements (aria-activedescendant).
let uid = 0;
const nextUid = () => `mfe-${(uid += 1)}`;

// ============================================================================
// Tiptap custom node — MergeChip
// Renders as a non-editable inline <span> styled as a pill/chip.
// Stored in HTML as: <span data-merge-token="{client.name}">[Client Name]</span>
// ============================================================================

const MergeChipNode = Node.create({
  name: 'mergeChip',
  group: 'inline',
  inline: true,
  atom: true, // treated as a single unit — cannot be partially selected

  addAttributes() {
    return {
      token: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-merge-token'),
        renderHTML: (attrs) => ({ 'data-merge-token': attrs.token }),
      },
      label: {
        default: '',
        parseHTML: (el) => el.textContent ?? '',
        renderHTML: () => ({}), // label is rendered as text content, not an attribute
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-merge-token]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class:
          'merge-chip inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ' +
          'bg-primary/10 text-primary border border-primary/20 ' +
          'select-none cursor-default mx-0.5',
        contenteditable: 'false',
      }),
      node.attrs.label as string,
    ];
  },

  renderText({ node }) {
    // When exporting as plain text, use the friendly label
    return node.attrs.label as string;
  },
});

// ============================================================================
// { trigger suggestion extension state
// ============================================================================

interface SuggestionState {
  active: boolean;
  query: string;
  /** `from` = position of the opening `{`. */
  range: { from: number; to: number } | null;
  rect: DOMRect | null;
}

const DEFAULT_SUGGESTION: SuggestionState = {
  active: false,
  query: '',
  range: null,
  rect: null,
};

// ============================================================================
// Floating suggestion dropdown (autocomplete)
// ============================================================================

interface SuggestionDropdownProps {
  tokens: MergeToken[];
  query: string;
  rect: DOMRect;
  selectedIndex: number;
  listboxId: string;
  optionId: (index: number) => string;
  onSelect: (token: MergeToken) => void;
}

const SuggestionDropdown = ({
  tokens,
  query,
  rect,
  selectedIndex,
  listboxId,
  optionId,
  onSelect,
}: SuggestionDropdownProps) => {
  if (tokens.length === 0) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed z-[9999] w-72 rounded-md border border-border bg-popover shadow-lg overflow-hidden"
      style={{
        top: rect.bottom + 4 + window.scrollY,
        left: rect.left + window.scrollX,
      }}
    >
      <div className="px-2 py-1.5 border-b border-border bg-muted/50">
        <span className="text-xs text-muted-foreground font-medium">
          {query ? `Matching "${query}"` : 'Type to filter variables…'}
        </span>
      </div>
      <ul
        id={listboxId}
        role="listbox"
        aria-label="Merge fields"
        className="max-h-56 overflow-y-auto py-1"
      >
        {tokens.map((token, i) => (
          <li
            key={token.token}
            id={optionId(i)}
            role="option"
            aria-selected={i === selectedIndex}
            aria-label={`${tokenDisplayName(token)}. ${token.description}`}
          >
            <button
              type="button"
              tabIndex={-1}
              className={cn(
                'w-full text-left px-3 py-1.5 flex flex-col gap-0.5 hover:bg-accent',
                i === selectedIndex && 'bg-accent',
              )}
              onMouseDown={(e) => {
                e.preventDefault(); // keep editor focus
                onSelect(token);
              }}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium leading-tight">
                  {tokenDisplayName(token)}
                </span>
                <code className="text-[10px] text-muted-foreground/80 font-mono shrink-0">
                  {token.token}
                </code>
              </span>
              {token.description && (
                <span className="text-xs text-muted-foreground leading-tight">
                  {token.description}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
      <div className="px-2 py-1 border-t border-border bg-muted/30">
        <span className="text-[10px] text-muted-foreground">
          ↑↓ navigate · Enter/Tab select · Esc dismiss
        </span>
      </div>
    </div>,
    document.body,
  );
};

// ============================================================================
// Toolbar button (matches the style used in RichTextEditor)
// ============================================================================

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
  className?: string;
}

const ToolbarButton = ({
  onClick,
  isActive,
  disabled,
  title,
  children,
  className,
}: ToolbarButtonProps) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'h-7 w-7 p-0 rounded',
      isActive
        ? 'bg-primary/10 text-primary hover:bg-primary/15'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent',
      className,
    )}
  >
    {children}
  </Button>
);

// ============================================================================
// MergeFieldEditor props + imperative handle
// ============================================================================

export interface MergeFieldEditorHandle {
  /** Insert a token chip at the current cursor position. */
  insertToken: (token: string) => void;
  /** Focus the editor. */
  focus: () => void;
}

export interface MergeFieldEditorProps {
  /** HTML string content (may contain merge chip spans) */
  content: string;
  /** Called with updated HTML on every change */
  onChange: (html: string) => void;
  /**
   * The tokens available in the "Insert field ▼" dropdown and autocomplete.
   * Pass a scoped subset from `getMergeTokensForScope(scope)`.
   */
  availableTokens: MergeToken[];
  /** Maximum character count */
  maxLength?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Additional class names for the outer wrapper */
  className?: string;
  /** When true the editor is read-only */
  disabled?: boolean;
  /** Called when the editor gains focus */
  onFocus?: () => void;
}

// ============================================================================
// MergeFieldEditor component
// ============================================================================

const MergeFieldEditor = React.forwardRef<MergeFieldEditorHandle, MergeFieldEditorProps>(
  (
    { content, onChange, availableTokens, maxLength, placeholder, className, disabled, onFocus },
    ref,
  ) => {
    // ── Suggestion state ───────────────────────────────────────────────────
    const [suggestion, setSuggestion] = React.useState<SuggestionState>(DEFAULT_SUGGESTION);
    const [suggestionSelectedIndex, setSuggestionSelectedIndex] = React.useState(0);
    const suggestionRef = React.useRef<SuggestionState>(DEFAULT_SUGGESTION);
    suggestionRef.current = suggestion;

    // Stable ids for aria wiring.
    const listboxId = React.useRef(nextUid()).current;
    const optionId = React.useCallback((i: number) => `${listboxId}-opt-${i}`, [listboxId]);

    // ── { trigger Tiptap Extension ─────────────────────────────────────────
    const TriggerExtension = React.useMemo(
      () =>
        Extension.create({
          name: 'mergeTrigger',
          addKeyboardShortcuts() {
            return {
              '{': () => {
                const { state } = this.editor;
                const { from, empty } = state.selection;
                if (!empty) return false; // let default replace the selection
                // Insert an auto-closing brace and drop the cursor between them.
                this.editor.chain().insertContent('{}').setTextSelection(from + 1).run();
                setTimeout(() => {
                  const sel = window.getSelection();
                  const rect =
                    sel && sel.rangeCount > 0
                      ? sel.getRangeAt(0).getBoundingClientRect()
                      : null;
                  setSuggestion({
                    active: true,
                    query: '',
                    range: { from, to: from + 2 },
                    rect,
                  });
                  setSuggestionSelectedIndex(0);
                }, 0);
                return true; // we inserted the brace ourselves
              },
              Escape: () => {
                if (suggestionRef.current.active) {
                  setSuggestion(DEFAULT_SUGGESTION);
                  return true;
                }
                return false;
              },
              ArrowDown: () => {
                if (!suggestionRef.current.active) return false;
                setSuggestionSelectedIndex((i) => i + 1);
                return true;
              },
              ArrowUp: () => {
                if (!suggestionRef.current.active) return false;
                setSuggestionSelectedIndex((i) => Math.max(0, i - 1));
                return true;
              },
              // Enter / Tab selection handled via the window keydown effect below
              // so it can read the current filtered list.
            };
          },
        }),
      [],
    );

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          hardBreak: false,
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            class: 'text-info-500 underline cursor-pointer',
          },
        }),
        MergeChipNode,
        TriggerExtension,
        ...(maxLength !== undefined
          ? [CharacterCount.configure({ limit: maxLength })]
          : [CharacterCount]),
      ],
      content,
      editable: !disabled,
      onFocus() {
        onFocus?.();
      },
      onUpdate({ editor: e }) {
        const html = e.isEmpty ? '' : e.getHTML();
        onChange(html);

        // Track query text after the '{' trigger.
        const curr = suggestionRef.current;
        if (curr.active && curr.range) {
          const { from } = e.state.selection;
          const triggerStart = curr.range.from;
          if (from > triggerStart) {
            const text = e.state.doc.textBetween(triggerStart, from, ' ');
            const query = text.startsWith('{') ? text.slice(1) : text;
            // A space / brace ends the token.
            if (query.includes(' ') || query.includes('}') || query.includes('{')) {
              setSuggestion(DEFAULT_SUGGESTION);
            } else {
              const sel = window.getSelection();
              const rect =
                sel && sel.rangeCount > 0
                  ? sel.getRangeAt(0).getBoundingClientRect()
                  : curr.rect;
              setSuggestion((s) => ({
                ...s,
                query,
                range: { from: triggerStart, to: from },
                rect: rect ?? s.rect,
              }));
              setSuggestionSelectedIndex(0);
            }
          } else {
            // Cursor moved before the trigger — dismiss.
            setSuggestion(DEFAULT_SUGGESTION);
          }
        }
      },
      editorProps: {
        attributes: {
          class: cn(
            'prose prose-sm dark:prose-invert max-w-none',
            'min-h-[80px] px-3 py-2 text-sm outline-none focus:outline-none',
          ),
          role: 'textbox',
          'aria-multiline': 'true',
          ...(placeholder ? { 'data-placeholder': placeholder } : {}),
        },
      },
    });

    // Sync external content changes
    React.useEffect(() => {
      if (!editor) return;
      const current = editor.isEmpty ? '' : editor.getHTML();
      if (content !== current) {
        editor.commands.setContent(content ?? '', false);
      }
    }, [content, editor]);

    // Sync disabled prop
    React.useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [disabled, editor]);

    // --- Link helper ---
    const handleSetLink = React.useCallback(() => {
      if (!editor) return;
      const previousUrl = editor.getAttributes('link').href as string | undefined;
      const url = window.prompt('Enter URL', previousUrl ?? 'https://');
      if (url === null) return;
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    // --- Insert merge chip (from toolbar dropdown, side panel, or { trigger) ---
    const insertToken = React.useCallback(
      (mergeToken: MergeToken, fromTrigger = false) => {
        if (!editor) return;
        const curr = suggestionRef.current;

        if (fromTrigger && curr.active && curr.range) {
          const { state } = editor;
          let to = state.selection.from;
          // Consume the auto-inserted closing brace, if still present.
          const nextChar = state.doc.textBetween(
            to,
            Math.min(to + 1, state.doc.content.size),
          );
          if (nextChar === '}') to += 1;
          editor
            .chain()
            .focus()
            .deleteRange({ from: curr.range.from, to })
            .insertContent({
              type: 'mergeChip',
              attrs: { token: mergeToken.token, label: mergeToken.label },
            })
            .run();
        } else {
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'mergeChip',
              attrs: { token: mergeToken.token, label: mergeToken.label },
            })
            .run();
        }
        setSuggestion(DEFAULT_SUGGESTION);
      },
      [editor],
    );

    // --- Imperative handle for side-panel insertion ---
    React.useImperativeHandle(
      ref,
      () => ({
        insertToken: (tokenStr: string) => {
          const def = availableTokens.find((t) => t.token === tokenStr);
          if (def) insertToken(def, false);
        },
        focus: () => editor?.commands.focus(),
      }),
      [availableTokens, insertToken, editor],
    );

    // Fuzzy-filtered tokens for the autocomplete (shared with the side panel).
    const filteredSuggestionTokens = React.useMemo(() => {
      if (!suggestion.active) return [];
      return filterTokens(availableTokens, suggestion.query);
    }, [suggestion.active, suggestion.query, availableTokens]);

    // Keep the selected index in bounds as the filtered list changes.
    const clampedIndex = Math.min(
      suggestionSelectedIndex,
      Math.max(0, filteredSuggestionTokens.length - 1),
    );

    // Handle Enter / Tab selection (reads current filtered list).
    React.useEffect(() => {
      if (!suggestion.active || !editor) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === 'Tab') && filteredSuggestionTokens.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          const token = filteredSuggestionTokens[clampedIndex];
          if (token) insertToken(token, true);
        }
      };
      window.addEventListener('keydown', handleKeyDown, true);
      return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [suggestion.active, filteredSuggestionTokens, clampedIndex, insertToken, editor]);

    // Wire aria-activedescendant / aria-expanded onto the contenteditable.
    React.useEffect(() => {
      if (!editor) return;
      const dom = editor.view.dom as HTMLElement;
      if (suggestion.active && filteredSuggestionTokens.length > 0) {
        dom.setAttribute('aria-expanded', 'true');
        dom.setAttribute('aria-controls', listboxId);
        dom.setAttribute('aria-activedescendant', optionId(clampedIndex));
      } else {
        dom.removeAttribute('aria-expanded');
        dom.removeAttribute('aria-controls');
        dom.removeAttribute('aria-activedescendant');
      }
    }, [editor, suggestion.active, filteredSuggestionTokens.length, clampedIndex, listboxId, optionId]);

    // Group tokens by category, respecting CATEGORY_ORDER
    const tokensByCategory = React.useMemo(() => {
      const grouped = new Map<MergeTokenCategory, MergeToken[]>();
      for (const cat of CATEGORY_ORDER) {
        const tokens = availableTokens.filter((t) => t.category === cat);
        if (tokens.length > 0) {
          grouped.set(cat, tokens);
        }
      }
      return grouped;
    }, [availableTokens]);

    // Character count
    const charCount = editor?.storage.characterCount?.characters?.() ?? 0;
    const isNearLimit = maxLength !== undefined && charCount >= maxLength * 0.9;
    const isAtLimit = maxLength !== undefined && charCount >= maxLength;

    return (
      <div
        className={cn(
          'rounded-md border border-input bg-background shadow-xs',
          'focus-within:border-ring focus-within:ring-ring focus-within:ring-[3px]',
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
      >
        {/* ── Toolbar ──────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1">
          <ToolbarButton
            title="Bold (Ctrl+B)"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            isActive={editor?.isActive('bold')}
            disabled={!editor}
          >
            <Bold className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Italic (Ctrl+I)"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            isActive={editor?.isActive('italic')}
            disabled={!editor}
          >
            <Italic className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Underline (Ctrl+U)"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            isActive={editor?.isActive('underline')}
            disabled={!editor}
          >
            <UnderlineIcon className="size-3.5" />
          </ToolbarButton>

          <div className="mx-1 h-4 w-px bg-border" />

          <ToolbarButton
            title="Bullet List"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            isActive={editor?.isActive('bulletList')}
            disabled={!editor}
          >
            <List className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Numbered List"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            isActive={editor?.isActive('orderedList')}
            disabled={!editor}
          >
            <ListOrdered className="size-3.5" />
          </ToolbarButton>

          <div className="mx-1 h-4 w-px bg-border" />

          <ToolbarButton
            title="Insert / Edit Link"
            onClick={handleSetLink}
            isActive={editor?.isActive('link')}
            disabled={!editor}
          >
            <LinkIcon className="size-3.5" />
          </ToolbarButton>

          <ToolbarButton
            title="Clear Formatting"
            onClick={() =>
              editor?.chain().focus().clearNodes().unsetAllMarks().run()
            }
            disabled={!editor}
          >
            <RemoveFormatting className="size-3.5" />
          </ToolbarButton>

          {/* ── Insert field dropdown ─────────────────────────── */}
          {availableTokens.length > 0 && (
            <>
              <div className="mx-1 h-4 w-px bg-border" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!editor}
                    className={cn(
                      'h-7 gap-1 rounded px-2 text-xs font-normal',
                      'text-muted-foreground hover:text-foreground hover:bg-accent',
                    )}
                  >
                    <Tag className="size-3.5" />
                    Insert field
                    <ChevronDown className="size-3" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="start"
                  className="w-64 max-h-80 overflow-y-auto"
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  {Array.from(tokensByCategory.entries()).map(
                    ([category, tokens], groupIndex) => (
                      <React.Fragment key={category}>
                        {groupIndex > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                          {CATEGORY_LABELS[category] ?? category}
                        </DropdownMenuLabel>
                        {tokens.map((mergeToken) => (
                          <DropdownMenuItem
                            key={mergeToken.token}
                            onSelect={() => insertToken(mergeToken)}
                            className="flex flex-col items-start gap-0.5 cursor-pointer"
                          >
                            <span className="text-sm font-medium">
                              {tokenDisplayName(mergeToken)}
                            </span>
                            {mergeToken.description && (
                              <span className="text-xs text-muted-foreground leading-tight">
                                {mergeToken.description}
                              </span>
                            )}
                          </DropdownMenuItem>
                        ))}
                      </React.Fragment>
                    ),
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>

        {/* ── Editor area ──────────────────────────────────────── */}
        <EditorContent editor={editor} />

        {/* ── Character count ──────────────────────────────────── */}
        {maxLength !== undefined && (
          <div className="flex justify-end border-t border-border px-3 py-1">
            <span
              className={cn(
                'text-xs tabular-nums',
                isAtLimit
                  ? 'text-destructive'
                  : isNearLimit
                    ? 'text-warning-600 dark:text-warning-500'
                    : 'text-muted-foreground',
              )}
            >
              {charCount}/{maxLength}
            </span>
          </div>
        )}

        {/* ── { trigger suggestion dropdown (portal) ───────────── */}
        {suggestion.active && suggestion.rect && (
          <SuggestionDropdown
            tokens={filteredSuggestionTokens}
            query={suggestion.query}
            rect={suggestion.rect}
            selectedIndex={clampedIndex}
            listboxId={listboxId}
            optionId={optionId}
            onSelect={(token) => insertToken(token, true)}
          />
        )}
      </div>
    );
  },
);

MergeFieldEditor.displayName = 'MergeFieldEditor';

export { MergeFieldEditor };
